import { useLogEvents } from '@monorepo/complianceCalendar/src/hooks/useLogEvents';
import { endOfDay, startOfDay } from 'date-fns';
import {
  ComplianceCalendarEdpLogEvent,
  ComplianceCalendarEventStatus,
  DateLike,
  FrequencyRequirementResponse,
  IntervalFrequencyEnum,
  localEquivalentOfUTC,
  NonEmptyArray,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import { useMemo } from 'react';

interface GroupedByFrequencyRequirementLogEvent {
  frequencyRequirement: FrequencyRequirementResponse;
  status: ComplianceCalendarEventStatus;
  completedEventCount: number;
  requiredEventCount: number;
}

type FrequencyRequirementId = string;

type PeriodicEvent = Omit<
  ComplianceCalendarEdpLogEvent,
  'frequencyRequirement'
> & {
  frequencyRequirement: FrequencyRequirementResponse;
};

interface UseGroupedByFrequencyRequirementLogEventsProps {
  dueDate?: DateLike | null;
  logId: string;
  projectId?: string;
}

interface UseGroupedByFrequencyRequirementLogEventsResponse {
  isLoading: boolean;
  groupedEvents: GroupedByFrequencyRequirementLogEvent[];
  asNeededEvents: ComplianceCalendarEdpLogEvent[];
}

function isPeriodicEvent(
  event: ComplianceCalendarEdpLogEvent,
): event is PeriodicEvent {
  return (
    !!event.frequencyRequirement &&
    event.frequencyRequirement.frequency.intervalFrequency !==
      IntervalFrequencyEnum.AS_NEEDED
  );
}

function getDayStart(date?: DateLike | null) {
  if (!date) return undefined;
  return UTCEquivalentOfLocal(
    startOfDay(localEquivalentOfUTC(new Date(date))),
  ).toISOString();
}

function getDayEnd(date?: DateLike | null) {
  if (!date) return undefined;
  return UTCEquivalentOfLocal(
    endOfDay(localEquivalentOfUTC(new Date(date))),
  ).toISOString();
}

function getStatusForGroup(
  group: PeriodicEvent[],
): ComplianceCalendarEventStatus {
  const isStarted = group.some(
    (g) =>
      g.status === ComplianceCalendarEventStatus.COMPLETE ||
      g.status === ComplianceCalendarEventStatus.OVERDUE_BUT_STARTED ||
      g.status === ComplianceCalendarEventStatus.INCOMPLETE_BUT_STARTED,
  );

  const someOverdue = group.some(
    (g) =>
      g.status === ComplianceCalendarEventStatus.OVERDUE_NOT_STARTED ||
      g.status === ComplianceCalendarEventStatus.OVERDUE_BUT_STARTED,
  );

  if (someOverdue) {
    return isStarted
      ? ComplianceCalendarEventStatus.OVERDUE_BUT_STARTED
      : ComplianceCalendarEventStatus.OVERDUE_NOT_STARTED;
  }

  const allRequiredCompleted = group.every(
    (g) =>
      g.status === ComplianceCalendarEventStatus.COMPLETE ||
      g.status === ComplianceCalendarEventStatus.NOT_NEEDED,
  );
  if (allRequiredCompleted) {
    return ComplianceCalendarEventStatus.COMPLETE;
  }

  return isStarted
    ? ComplianceCalendarEventStatus.INCOMPLETE_BUT_STARTED
    : ComplianceCalendarEventStatus.INCOMPLETE_NOT_STARTED;
}

export function useGroupedByFrequencyRequirementLogEvents({
  dueDate,
  logId,
  projectId,
}: UseGroupedByFrequencyRequirementLogEventsProps): UseGroupedByFrequencyRequirementLogEventsResponse {
  const { isLoading, logEvents } = useLogEvents({
    logId,
    projectId,
    startDate: getDayStart(dueDate),
    endDate: getDayEnd(dueDate),
  });

  const groupedEvents = useMemo(() => {
    if (!logEvents) return [] as GroupedByFrequencyRequirementLogEvent[];
    const periodicEvents: PeriodicEvent[] = logEvents.filter(isPeriodicEvent);
    const groupedByFrequencyRequirementId = periodicEvents.reduce<
      Record<FrequencyRequirementId, NonEmptyArray<PeriodicEvent>>
    >((grouped, event) => {
      const freqReqId = event.frequencyRequirement.id;
      return {
        ...grouped,
        [freqReqId]: [...(grouped[freqReqId] || []), event],
      };
    }, {});
    return Object.values(groupedByFrequencyRequirementId).map((group) => ({
      frequencyRequirement: group[0].frequencyRequirement,
      status: getStatusForGroup(group),
      completedEventCount: group.filter(
        (e) => e.status === ComplianceCalendarEventStatus.COMPLETE,
      ).length,
      requiredEventCount:
        group[0].frequencyRequirement.frequency.customFrequency
          ?.numberOfEvents || 1,
    }));
  }, [logEvents]);

  const asNeededEvents = useMemo(() => {
    if (!logEvents) return [] as ComplianceCalendarEdpLogEvent[];
    return logEvents.filter(
      (e) =>
        !e.frequencyRequirement ||
        e.frequencyRequirement.frequency.intervalFrequency ===
          IntervalFrequencyEnum.AS_NEEDED,
    );
  }, [logEvents]);

  return {
    isLoading,
    groupedEvents,
    asNeededEvents,
  };
}
